import {
  DataType,
  FormField,
} from '@labrav/react-components/lib/@types/types/forms'
import { industryOptions } from '../../common/utils/CoSellHelpers/constants'
import HubSpotLogo from '../../common/images/HubSpot_Logo.svg'
import LabraLogo from '../../common/images/new_labra_logo_compact.svg'
import SalesforceLogo from '../../common/images/salesforce.svg'
import AwsLogo from '../../common/images/awsCloudNew.svg'
import AzureLogo from '../../common/images/azureCloudNew.svg'
import type {
  HighlightMetricsFormValues,
  AwsMetricEntry,
} from '../../oppsync/components/CoSell/PartnerIQ/AdminPanel/SectionEditContent/HighlightMetrics/HighlightMetricsEditModal.types'
import { AWS_METRIC_DEFINITIONS } from '../../oppsync/components/CoSell/PartnerIQ/AdminPanel/SectionEditContent/HighlightMetrics/HighlightMetricsEditModal.types'

export const annualRevenueOptions = [
  'Less than $5M',
  '$5M-$10M',
  '$10M-$50M',
  '$50M-$100M',
  '$100M-$500M',
  '$500M-$1B',
  'More than $1B',
]
export const employeeSizeOptions = [
  '1-50',
  '51-200',
  '201-500',
  '501-1000',
  '1001-5000',
  '5001-10000',
  'More than 10000',
]
export const segmentOptions = [
  'SMB',
  'Mid-Market',
  'Enterprise',
  'Strategic Enterprise',
  'Public Sector',
  'Startup',
]
export const cloudTechnologyStackOptions = ['AWS', 'Azure', 'Google Cloud']
export const geographicalRegionOptions = [
  'North America',
  'Europe',
  'Asia Pacific',
  'Middle East',
  'Latin America',
  'Africa',
  'Global',
]
export const marketplacePresenceOptions = ['AWS', 'Azure', 'GCP']

export const getMockPartnerIQFormFields = (): Array<
  FormField & { gridSize: { xs: number } }
> => [
  {
    id: 'icpName',
    dataType: 'string',
    title: 'ICP Name',
    placeHolder: 'ICP Name',
    isRequired: false,
    gridSize: { xs: 12 },
    humanReadableKey: 'icpName',
    isReadOnly: false,
    isCustom: false,
  },
  {
    id: 'annualRevenue',
    dataType: 'multi_enum' as DataType,
    title: 'Annual Revenue',
    placeHolder: 'Annual Revenue',
    isRequired: false,
    options: annualRevenueOptions,
    gridSize: { xs: 12 },
    humanReadableKey: 'annualRevenue',
    isReadOnly: false,
    isCustom: false,
  },
  {
    id: 'industry',
    dataType: 'multi_enum' as DataType,
    title: 'Industry',
    placeHolder: 'Industry',
    isRequired: false,
    options: industryOptions,
    gridSize: { xs: 12 },
    humanReadableKey: 'industry',
    isReadOnly: false,
    isCustom: false,
  },
  {
    id: 'employeeSize',
    dataType: 'multi_enum' as DataType,
    title: 'Employee Size',
    options: employeeSizeOptions,
    placeHolder: 'Select Employee Size',
    isRequired: false,
    gridSize: { xs: 12 },
    humanReadableKey: 'employeeSize',
    isReadOnly: false,
    isCustom: false,
  },
  {
    id: 'segment',
    dataType: 'multi_enum' as DataType,
    title: 'Segment',
    placeHolder: 'Segment',
    isRequired: false,
    options: segmentOptions,
    gridSize: { xs: 12 },
    humanReadableKey: 'segment',
    isReadOnly: false,
    isCustom: false,
  },
  {
    id: 'cloudTechnologyStack',
    title: 'Cloud Technology Stack',
    placeHolder: 'Cloud Technology Stack',
    isRequired: false,
    options: cloudTechnologyStackOptions,
    humanReadableKey: 'cloudTechnologyStack',
    isReadOnly: false,
    defaultValue: undefined,
    dataType: 'multi_enum' as DataType,
    validationType: undefined,
    isCustom: false,
    gridSize: { xs: 12 },
  },
  {
    id: 'geographicalRegion',
    dataType: 'multi_enum' as DataType,
    title: 'Geographical Region',
    placeHolder: 'Geographical Region',
    isRequired: false,
    options: geographicalRegionOptions,
    gridSize: { xs: 12 },
    humanReadableKey: 'geographicalRegion',
    isReadOnly: false,
    isCustom: false,
  },
  {
    id: 'marketplacePresence',
    dataType: 'multi_enum' as DataType,
    title: 'Marketplace Presence',
    placeHolder: 'Marketplace Presence',
    isRequired: false,
    options: marketplacePresenceOptions,
    gridSize: { xs: 12 },
    humanReadableKey: 'marketplacePresence',
    isReadOnly: false,
    isCustom: false,
  },
]

export const getTopPerformerDescriptionTitle = 'Top Performer'
export const getTopPerformerDescriptionDescription =
  'Recognized based on a weighted mix of:'
export const getTopPerformerDescriptionRevenueContribution =
  '• Revenue contribution – 33.4%'
export const getTopPerformerDescriptionTimeTakenToCloseDeals =
  '• Time taken to close deals – 33.3%'
export const getTopPerformerDescriptionWinLossPerformance =
  '• Win/Loss performance – 33.3%'

export type StatCardData = {
  label: string
  value: string
  showGradient?: boolean
  isVisible?: boolean
}

export type CollabRoomCompanyData = {
  companyName: string
  description: string
  linkedinUrl: string
  websiteUrl: string
  websiteLabel: string
  stats: StatCardData[]
  sectionHidden?: boolean
  companyLogo?: string
}

export const collabRoomMockData: CollabRoomCompanyData = {
  companyName: 'Labra Ventures',
  sectionHidden: false,
  description:
    'Labra Ventures is a global technology and innovation company with a rich history of delivering enterprise solutions. As an AWS Premier Consulting Partner, Labra Ventures helps organizations migrate, modernize, and innovate on AWS cloud infrastructure.',
  linkedinUrl: 'https://linkedin.com/company/labra-ventures',
  websiteUrl: 'https://labra.io',
  websiteLabel: 'labra.io',
  stats: [
    {
      label: 'Total Co-sell revenue',
      value: '$420M',
      showGradient: true,
    },
    {
      label: 'AWS originated opportunities',
      value: '1,250',
    },
    {
      label: 'Labra originated opportunities',
      value: '2,550',
    },
  ],
}

/** Single form state shape for Company Details edit modal – one object for mock + state */
export type CompanyDetailsFormData = {
  companyName: string
  linkedinUrl: string
  websiteUrl: string
  description: string
  syncedBadges: StatCardData[]
  customBadges: Array<{ label: string; value: string; isVisible?: boolean }>
  companyLogo?: string
}

/** Default JSON used to render the form and initialize form state */
export const companyDetailsFormDefault: CompanyDetailsFormData = {
  companyName: collabRoomMockData.companyName,
  linkedinUrl: collabRoomMockData.linkedinUrl,
  websiteUrl: collabRoomMockData.websiteUrl,
  description: collabRoomMockData.description,
  syncedBadges: collabRoomMockData.stats.map(s => ({
    ...s,
    isVisible: true,
  })),
  customBadges: [
    { label: '', value: '', isVisible: true },
    { label: '', value: '', isVisible: true },
  ],
}

export const trustedByMockData = [
  {
    companyName: 'Labra Ventures',
    description:
      'Labra Ventures is a global technology and innovation company with a rich history of delivering enterprise solutions. As an AWS Premier Consulting Partner, Labra Ventures helps organizations migrate, modernize, and innovate on AWS cloud infrastructure.',
    linkedinUrl: 'https://linkedin.com/company/labra-ventures',
    websiteUrl: 'https://labra.io',
    websiteLabel: 'labra.io',
  },
  {
    companyName: 'Labra Ventures',
    description:
      'Labra Ventures is a global technology and innovation company with a rich history of delivering enterprise solutions. As an AWS Premier Consulting Partner, Labra Ventures helps organizations migrate, modernize, and innovate on AWS cloud infrastructure.',
    linkedinUrl: 'https://linkedin.com/company/labra-ventures',
    websiteUrl: 'https://labra.io',
    websiteLabel: 'labra.io',
  },
]

export const trustedCompanyLogos = [
  { name: 'HubSpot', logo: HubSpotLogo },
  { name: 'Labra', logo: LabraLogo },
  { name: 'Salesforce', logo: SalesforceLogo },
  { name: 'HubSpot', logo: HubSpotLogo },
  { name: 'AWS', logo: AwsLogo },
  { name: 'Azure', logo: AzureLogo },
]

export const testimonials = [
  {
    quote:
      'Once we implemented Acme, it removed the need to share or maintain opportunities ourselves.Once we implemented Acme, it removed the need to share or maintain opportunities ourselves.',
    name: 'Nestor Fernandez',
    title: 'Partner Operations Manager',
    company: 'Udemy',
    companyLogo: HubSpotLogo,
  },
  {
    quote:
      'With the Acme AC lifecycle time by 90 further and we expOnce we implemented Acme, it removed the need to share or maintain opportunities ourselves.',
    name: 'IBM Brenno',
    title: 'Global A',
    company: 'IBM',
    companyLogo: SalesforceLogo,
    profilePicture: undefined,
  },
  {
    quote:
      'With the Acme AC lifecycle time by 90 further and we expOnce we implemented Acme, it removed the need to share or maintain opportunities ourselves.',
    name: 'IBM Brenno',
    title: 'Global A',
    company: 'IBM',
    companyLogo: SalesforceLogo,
    profilePicture: undefined,
  },
]

export type ProfileCardTeamData = {
  teamName: string
  members: ProfileCardMemberData[]
}

export type ProfileCardMemberData = {
  id?: string
  name: string
  role: string
  profilePicture?: string
  email?: string
  isHidden?: boolean
}

export const teamMockData: ProfileCardTeamData[] = [
  {
    teamName: 'Acme Inc. alliance team',
    members: [
      {
        name: 'Floyd Miles',
        role: 'Alliance lead',
        email: 'floyd.miles@acme.com',
        profilePicture:
          'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=100&h=100&fit=crop',
      },
      {
        name: 'Darrell Steward',
        role: 'Alliance team member',
        email: 'darrell.steward@acme.com',
        profilePicture:
          'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=100&h=100&fit=crop',
      },
      {
        name: 'Courtney Henry',
        role: 'Account Executive',
        email: 'courtney.henry@acme.com',
        profilePicture:
          'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=100&h=100&fit=crop',
      },
      {
        name: 'Darrell Steward',
        role: 'Alliance team member',
        email: 'darrell.steward@acme.com',
        profilePicture:
          'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=100&h=100&fit=crop',
      },
      {
        name: 'Courtney Henry',
        role: 'Account Executive',
        email: 'courtney.henry@acme.com',
        profilePicture:
          'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=100&h=100&fit=crop',
      },
      {
        name: 'Courtney Henry',
        role: 'Account Executive',
        email: 'courtney.henry@acme.com',
        profilePicture:
          'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=100&h=100&fit=crop',
      },
      {
        name: 'Darrell Steward',
        role: 'Alliance team member',
        email: 'darrell.steward@acme.com',
        profilePicture:
          'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=100&h=100&fit=crop',
      },
      {
        name: 'Courtney Henry',
        role: 'Account Executive',
        email: 'courtney.henry@acme.com',
        profilePicture:
          'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=100&h=100&fit=crop',
      },
      {
        name: 'Courtney Henry',
        role: 'Account Executive',
        email: 'courtney.henry@acme.com',
        profilePicture:
          'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=100&h=100&fit=crop',
      },
    ],
  },
  {
    teamName: 'Acme Inc. sales team',
    members: [
      {
        name: 'Floyd Miles',
        role: 'Account Executive',
        email: 'floyd.miles@acme.com',
        profilePicture:
          'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=100&h=100&fit=crop',
      },
      {
        name: 'Darrell Steward',
        role: 'Account Executive',
        email: 'darrell.steward@acme.com',
        profilePicture:
          'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=100&h=100&fit=crop',
      },
      {
        name: 'Courtney Henry',
        role: 'Account Executive',
        email: 'courtney.henry@acme.com',
      },
      {
        name: 'Floyd Miles',
        role: 'Account Executive',
        email: 'floyd.miles@acme.com',
        profilePicture:
          'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=100&h=100&fit=crop',
      },
      {
        name: 'Darrell Steward',
        role: 'Account Executive',
        email: 'darrell.steward@acme.com',
        profilePicture:
          'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=100&h=100&fit=crop',
      },
      {
        name: 'Courtney Henry',
        role: 'Account Executive',
        email: 'courtney.henry@acme.com',
      },
      {
        name: 'Darrell Steward',
        role: 'Account Executive',
        email: 'darrell.steward@acme.com',
        profilePicture:
          'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=100&h=100&fit=crop',
      },
      {
        name: 'Courtney Henry',
        role: 'Account Executive',
        email: 'courtney.henry@acme.com',
      },
    ],
  },
]

export type HighlightMetric = {
  value: string
  label: string
  showGradient: boolean
}

export type HighlightMetricsData = {
  partnerId: string
  companyName: string
  tier: string
  metrics: HighlightMetric[]
}

export const highlightMetricsMockData: HighlightMetricsData = {
  partnerId: '00123',
  companyName: 'Acme Inc.',
  tier: 'Premier tier',
  metrics: [
    { value: '5', label: 'AWS competencies', showGradient: false },
    { value: '6', label: 'Partner programs', showGradient: false },
    { value: '17', label: 'Service validations', showGradient: true },
    { value: '22', label: 'AWS certifications', showGradient: false },
    { value: '1.1k+', label: 'Customer launches', showGradient: false },
  ],
}

export type CollateralItemType = 'csv' | 'document' | 'link' | 'pdf'
export type CollateralItemAction = 'download' | 'link'

export type CollateralItem = {
  id: string
  type: CollateralItemType
  name: string
  date: string
  action: CollateralItemAction
  isHidden?: boolean
  /** ISO date string from API – preserved for save payload */
  uploadedAt?: string
  /** Source file URL (S3 or external link) – mapped to backend file_url */
  fileUrl?: string
}

export type CollateralTabId =
  | 'first-call-deck'
  | 'solution-briefs'
  | 'win-wires'

export type CollateralTab = {
  id: CollateralTabId
  label: string
}

export const collateralTabs: CollateralTab[] = [
  { id: 'first-call-deck', label: 'First call deck' },
  { id: 'solution-briefs', label: 'Solution briefs' },
  { id: 'win-wires', label: 'Win wires' },
]

export const collateralByTab: Record<CollateralTabId, CollateralItem[]> = {
  'first-call-deck': [
    {
      id: '1',
      type: 'csv',
      name: 'GTM Contacts',
      date: 'Nov 20, 2024',
      action: 'download',
    },
    {
      id: '2',
      type: 'document',
      name: 'Quarterly Financial Report',
      date: 'Nov 20, 2024',
      action: 'download',
    },
    {
      id: '3',
      type: 'link',
      name: 'HyperscalerGTM Value Prop + Messaging',
      date: 'Nov 19, 2024',
      action: 'link',
    },
    {
      id: '4',
      type: 'pdf',
      name: 'BDR Outbound Playbook & Sequences',
      date: 'Nov 11, 2024',
      action: 'download',
    },
    {
      id: '5',
      type: 'document',
      name: 'Azure Marketplace SaaS Listings',
      date: 'Nov 11, 2024',
      action: 'download',
    },
    {
      id: '6',
      type: 'pdf',
      name: 'HyperScalerGTM Outreach Sequences',
      date: 'Nov 11, 2024',
      action: 'download',
    },
    {
      id: '7',
      type: 'pdf',
      name: 'HyperScalerGTM Outreach Sequences',
      date: 'Nov 11, 2024',
      action: 'download',
    },
    {
      id: '8',
      type: 'pdf',
      name: 'HyperScalerGTM Outreach Sequences',
      date: 'Nov 11, 2024',
      action: 'download',
    },
    {
      id: '9',
      type: 'pdf',
      name: 'HyperScalerGTM Outreach Sequences',
      date: 'Nov 11, 2024',
      action: 'download',
    },
    {
      id: '10',
      type: 'pdf',
      name: 'HyperScalerGTM Outreach Sequences',
      date: 'Nov 11, 2024',
      action: 'download',
    },
  ],
  'solution-briefs': [
    {
      id: 's1',
      type: 'pdf',
      name: 'Solution Brief A',
      date: 'Nov 18, 2024',
      action: 'download',
    },
    {
      id: 's2',
      type: 'document',
      name: 'Solution Brief B',
      date: 'Nov 15, 2024',
      action: 'download',
    },
    {
      id: 's3',
      type: 'pdf',
      name: 'Solution Brief C',
      date: 'Nov 15, 2024',
      action: 'download',
    },
    {
      id: 's4',
      type: 'document',
      name: 'Solution Brief D',
      date: 'Nov 15, 2024',
      action: 'download',
    },
    {
      id: 's5',
      type: 'pdf',
      name: 'Solution Brief E',
      date: 'Nov 15, 2024',
      action: 'download',
    },
    {
      id: 's6',
      type: 'document',
      name: 'Solution Brief F',
      date: 'Nov 15, 2024',
      action: 'download',
    },
  ],
  'win-wires': [
    {
      id: 'w1',
      type: 'pdf',
      name: 'Win Wire Case Study 1',
      date: 'Nov 10, 2024',
      action: 'download',
    },
    {
      id: 'w2',
      type: 'pdf',
      name: 'Win Wire Case Study 2',
      date: 'Nov 10, 2024',
      action: 'download',
    },
    {
      id: 'w3',
      type: 'pdf',
      name: 'Win Wire Case Study 3',
      date: 'Nov 10, 2024',
      action: 'download',
    },
    {
      id: 'w4',
      type: 'pdf',
      name: 'Win Wire Case Study 4',
      date: 'Nov 10, 2024',
      action: 'download',
    },
    {
      id: 'w5',
      type: 'pdf',
      name: 'Win Wire Case Study 5',
      date: 'Nov 10, 2024',
      action: 'download',
    },
    {
      id: 'w6',
      type: 'pdf',
      name: 'Win Wire Case Study 6',
      date: 'Nov 10, 2024',
      action: 'download',
    },
  ],
}

export const solutionsMockData = [
  {
    id: '1',
    name: 'Acme application monitoring app',
    description:
      "Acme application monitoring app is an automated software solution that integrates a partner's CRM (Salesforce or HubSpot) with the AWS APN Customer Engagement (ACE) portal, streamlining the co-selling process.",
    solutionId: '1234567890',
    solutionType: ['Company-owned', 'Partner-owned'],
  },
  {
    id: '2',
    name: 'Acme application monitoring app',
    description:
      "Acme application monitoring app is an automated software solution that integrates a partner's CRM (Salesforce or HubSpot) with the AWS APN Customer Engagement (ACE) portal, streamlining the co-selling process.",
    solutionId: '1234567890',
    solutionType: ['Company-owned', 'Partner-owned'],
  },
]

// ---------------------------------------------------------------------------
// PartnerIQ page data – single source of truth in Admin Panel section order
// ---------------------------------------------------------------------------

/** Trusted By slice: customer logos + testimonials (same shape as edit modal). */
export type PartnerIQTrustedByData = {
  customerLogos: Array<{
    id: string
    url: string
    website?: string
    name?: string
    isHidden?: boolean
  }>
  testimonials: Array<{
    id: string
    quote: string
    name: string
    title: string
    companyLogo?: string
    profilePicture?: string
    isHidden?: boolean
  }>
  sectionHidden?: boolean
}

/** Highlight Metrics slice: form values + display-only companyName & tier. */
export type PartnerIQHighlightMetricsData = HighlightMetricsFormValues & {
  companyName: string
  tier: string
  sectionHidden?: boolean
}

/** Visibility flags for teams (from API teams object). */
export type TeamsVisibility = {
  allianceSectionHidden?: boolean
  salesSectionHidden?: boolean
}

/** Page data in the same order as Admin Panel / Collab Room sections. */
export type PartnerIQPageData = {
  companyInfo: CollabRoomCompanyData
  trustedBy: PartnerIQTrustedByData
  highlightMetrics: PartnerIQHighlightMetricsData
  allianceAndSales: ProfileCardTeamData[]
  teamsVisibility?: TeamsVisibility
  collateral: {
    tabs: typeof collateralTabs
    byTab: typeof collateralByTab
    sectionHidden?: boolean
    firstCallSectionHidden?: boolean
    solutionBriefsSectionHidden?: boolean
    winWiresSectionHidden?: boolean
  }
  solutions: Array<(typeof solutionsMockData)[number] & { isHidden?: boolean }>
  solutionsSectionHidden?: boolean
}

/** Empty page data – used as the initial/fallback state so the page renders
 *  with blank sections instead of placeholder mock content. */
export const partnerIQPageDataEmpty: PartnerIQPageData = {
  companyInfo: {
    companyName: '',
    description: '',
    linkedinUrl: '',
    websiteUrl: '',
    websiteLabel: '',
    stats: [],
  },
  trustedBy: {
    customerLogos: [],
    testimonials: [],
  },
  highlightMetrics: {
    companyName: '',
    tier: '',
    partnerId: '',
    recognitionBadgeFileLinks: [],
    awsMetrics: [],
    informationBadges: [],
  },
  allianceAndSales: [],
  collateral: {
    tabs: collateralTabs,
    byTab: {
      'first-call-deck': [],
      'solution-briefs': [],
      'win-wires': [],
    },
  },
  solutions: [],
}

/** Default page data built from existing mocks in Admin Panel order. Uses deep copies so edits do not mutate shared references. */
export const partnerIQPageDataDefault: PartnerIQPageData = {
  companyInfo: {
    ...collabRoomMockData,
    stats: collabRoomMockData.stats.map(s => ({ ...s })),
  },
  trustedBy: {
    customerLogos: trustedCompanyLogos.map((c, i) => ({
      id: `logo-${i}`,
      url: (c as { name: string; logo: string }).logo ?? '',
      name: c.name,
    })),
    testimonials: testimonials.map((t, i) => ({
      id: `t-${i}`,
      quote: t.quote,
      name: t.name,
      title: t.title,
      company: t.company,
      companyLogo: (t as { companyLogo?: string }).companyLogo ?? undefined,
      profilePicture: (t as { profilePicture?: string }).profilePicture,
    })),
  },
  highlightMetrics: {
    companyName: highlightMetricsMockData.companyName,
    tier: highlightMetricsMockData.tier,
    partnerId: highlightMetricsMockData.partnerId,
    recognitionBadgeFileLinks: [],
    awsMetrics: AWS_METRIC_DEFINITIONS.map(
      (def, i): AwsMetricEntry => ({
        ...def,
        value: highlightMetricsMockData.metrics[i]?.value ?? '',
        isVisible: true,
      })
    ),
    informationBadges: [],
  },
  allianceAndSales: teamMockData.map(team => ({
    teamName: team.teamName,
    members: team.members.map(m => ({ ...m })),
  })),
  collateral: {
    tabs: [...collateralTabs],
    byTab: {
      'first-call-deck': collateralByTab['first-call-deck'].map(item => ({
        ...item,
      })),
      'solution-briefs': collateralByTab['solution-briefs'].map(item => ({
        ...item,
      })),
      'win-wires': collateralByTab['win-wires'].map(item => ({ ...item })),
    },
  },
  solutions: solutionsMockData.map(s => ({
    ...s,
    solutionType: [...(s.solutionType ?? [])],
  })),
}
